import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import Papa from 'papaparse';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Chip,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Description as FileIcon,
  Delete as DeleteIcon,
  Info as InfoIcon
} from '@mui/icons-material';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const MAX_PREVIEW_ROWS = 10;

// eslint-disable-next-line max-lines-per-function
function CSVUploader({ onUpload, onError }) {
  const [parseProgress, setParseProgress] = useState(0);
  const [isParsing, setIsParsing] = useState(false);
  const [parsedData, setParsedData] = useState(null);
  const [fileInfo, setFileInfo] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  const [validationWarnings, setValidationWarnings] = useState([]);

  const validateCSV = (data) => {
    const errors = [];
    const warnings = [];

    // Check if data exists
    if (!data || !data.data || data.data.length === 0) {
      errors.push('CSV file is empty');
      return { errors, warnings };
    }

    // Check headers
    const headers = data.meta.fields || [];
    if (headers.length === 0) {
      errors.push('No headers found in CSV file');
      return { errors, warnings };
    }

    // Check for required fields (at least firstName or lastName or name)
    const hasNameField = headers.some(h =>
      h.toLowerCase().includes('name') ||
      h.toLowerCase().includes('first') ||
      h.toLowerCase().includes('last')
    );

    if (!hasNameField) {
      warnings.push('No name fields detected. Make sure to map fields correctly.');
    }

    // Check for email field
    const hasEmailField = headers.some(h =>
      h.toLowerCase().includes('email') ||
      h.toLowerCase().includes('mail')
    );

    if (!hasEmailField) {
      warnings.push('No email field detected. Entities will be created without email addresses.');
    }

    // Check for empty rows
    const emptyRows = data.data.filter((row) => {
      const values = Object.values(row).filter(v => v && v.toString().trim() !== '');
      return values.length === 0;
    }).length;

    if (emptyRows > 0) {
      warnings.push(`Found ${emptyRows} empty row(s) that will be skipped.`);
    }

    // Check for duplicate headers
    const headerCounts = {};
    headers.forEach(h => {
      headerCounts[h] = (headerCounts[h] || 0) + 1;
    });
    const duplicateHeaders = Object.entries(headerCounts)
      .filter(([_, count]) => count > 1)
      .map(([header, _]) => header);

    if (duplicateHeaders.length > 0) {
      errors.push(`Duplicate column headers found: ${duplicateHeaders.join(', ')}`);
    }

    // Check data consistency
    const rowsWithMissingData = data.data.filter((row) => {
      const values = Object.values(row);
      return values.some(v => v === null || v === undefined);
    }).length;

    if (rowsWithMissingData > 0) {
      warnings.push(`${rowsWithMissingData} row(s) have missing values in some columns.`);
    }

    // Check for reasonable number of rows
    if (data.data.length > 10000) {
      warnings.push(`Large file detected (${data.data.length} rows). Import may take several minutes.`);
    }

    return { errors, warnings };
  };

  const processFile = useCallback((file) => {
    console.log('[CSVUploader] processFile called with:', file);
    console.log('[CSVUploader] File name:', file?.name, 'size:', file?.size, 'type:', file?.type);

    setIsParsing(true);
    setParseProgress(0);
    setValidationErrors([]);
    setValidationWarnings([]);

    // Store file info
    setFileInfo({
      name: file.name,
      size: file.size,
      type: file.type,
      lastModified: new Date(file.lastModified).toLocaleString()
    });

    // Read file content and parse as text (fixes FileSystemFileHandle issue)
    const reader = new FileReader();
    reader.onload = (e) => {
      const csvText = e.target.result;
      console.log('[CSVUploader] File content preview (first 500 chars):', csvText?.substring(0, 500));

      // Parse the text content directly instead of file object
      Papa.parse(csvText, {
      header: true,
      dynamicTyping: true,
      skipEmptyLines: true,  // Changed from 'greedy' - was causing empty file errors
      complete: (results) => {
        console.log('[CSVUploader] Papa.parse results:', results);
        console.log('[CSVUploader] Data rows:', results?.data?.length);
        console.log('[CSVUploader] Headers:', results?.meta?.fields);

        setIsParsing(false);
        setParseProgress(100);

        // Validate the parsed data
        const { errors, warnings } = validateCSV(results);
        setValidationErrors(errors);
        setValidationWarnings(warnings);

        if (errors.length === 0) {
          // Clean the data
          const cleanedData = results.data.filter(row => {
            const values = Object.values(row).filter(v => v && v.toString().trim() !== '');
            return values.length > 0;
          });

          const processedData = {
            headers: results.meta.fields,
            rows: cleanedData,
            totalRows: cleanedData.length,
            originalRows: results.data.length,
            meta: results.meta
          };

          setParsedData(processedData);

          // Notify parent component with file size
          if (onUpload) {
            onUpload(processedData, file.size);
          }
        } else {
          if (onError) {
            onError(errors.join('; '));
          }
        }
      },
      error: (error, _file) => {
        console.log('[CSVUploader] Papa.parse ERROR:', error);
        setIsParsing(false);
        setValidationErrors([`Failed to parse CSV: ${error.message}`]);
        if (onError) {
          onError(`Failed to parse CSV: ${error.message}`);
        }
      },
      });
    };
    reader.onerror = (e) => {
      console.log('[CSVUploader] FileReader ERROR:', e);
      setIsParsing(false);
      setValidationErrors(['Failed to read file']);
    };
    reader.readAsText(file);
  }, [onUpload, onError]);

  const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
    // Handle rejected files
    if (rejectedFiles.length > 0) {
      const errors = rejectedFiles.map(rejection => {
        const error = rejection.errors[0];
        if (error.code === 'file-too-large') {
          return `File "${rejection.file.name}" is too large. Maximum size is 10MB.`;
        } else if (error.code === 'file-invalid-type') {
          return `File "${rejection.file.name}" is not a CSV file.`;
        }
        return `File "${rejection.file.name}" was rejected: ${error.message}`;
      });
      setValidationErrors(errors);
      if (onError) {
        onError(errors.join('; '));
      }
      return;
    }

    // Process accepted file
    if (acceptedFiles.length > 0) {
      processFile(acceptedFiles[0]);
    }
  }, [processFile, onError]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.csv'],
      'text/plain': ['.csv']
    },
    maxSize: MAX_FILE_SIZE,
    multiple: false
  });

  const clearFile = () => {
    setParsedData(null);
    setFileInfo(null);
    setValidationErrors([]);
    setValidationWarnings([]);
    setParseProgress(0);
  };

  return (
    <Box>
      {!parsedData ? (
        <Box>
          <Paper
            {...getRootProps()}
            elevation={isDragActive ? 8 : 2}
            sx={{
              p: 4,
              textAlign: 'center',
              cursor: 'pointer',
              backgroundColor: isDragActive ? 'action.hover' : 'background.paper',
              border: '2px dashed',
              borderColor: isDragActive ? 'primary.main' : 'divider',
              transition: 'all 0.3s ease',
              '&:hover': {
                borderColor: 'primary.light',
                backgroundColor: 'action.hover'
              }
            }}
          >
            <input {...getInputProps()} />
            <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              {isDragActive ? 'Drop your CSV file here' : 'Drag & drop a CSV file here'}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              or click to select a file
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Maximum file size: 10MB | Supported format: CSV
            </Typography>
          </Paper>

          <Alert severity="info" icon={<InfoIcon />} sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Need help getting started?</strong> Download the sample CSV template above to see all available fields and example data formats.
            </Typography>
          </Alert>

          {isParsing && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="body2" gutterBottom>
                Parsing CSV file...
              </Typography>
              <LinearProgress variant="determinate" value={parseProgress} />
            </Box>
          )}

          {validationErrors.length > 0 && (
            <Alert severity="error" sx={{ mt: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                CSV Validation Errors:
              </Typography>
              <List dense>
                {validationErrors.map((error, idx) => (
                  <ListItem key={idx}>
                    <ListItemIcon>
                      <ErrorIcon color="error" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={error} />
                  </ListItem>
                ))}
              </List>
            </Alert>
          )}
        </Box>
      ) : (
        <Box>
          <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <FileIcon color="primary" />
                <Box>
                  <Typography variant="subtitle1">
                    {fileInfo.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {(fileInfo.size / 1024).toFixed(2)} KB | {parsedData.totalRows} rows | {parsedData.headers.length} columns
                  </Typography>
                </Box>
              </Box>
              <Button
                startIcon={<DeleteIcon />}
                onClick={clearFile}
                color="error"
                size="small"
              >
                Remove
              </Button>
            </Box>
          </Paper>

          {validationWarnings.length > 0 && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Warnings:
              </Typography>
              <List dense>
                {validationWarnings.map((warning, idx) => (
                  <ListItem key={idx}>
                    <ListItemIcon>
                      <WarningIcon color="warning" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={warning} />
                  </ListItem>
                ))}
              </List>
            </Alert>
          )}

          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircleIcon color="success" />
            <Typography variant="body1">
              CSV file successfully parsed
            </Typography>
            <Chip
              label={`${parsedData.totalRows} rows ready for import`}
              color="success"
              size="small"
            />
          </Box>

          <Typography variant="subtitle2" gutterBottom sx={{ mt: 3 }}>
            Preview (first {Math.min(MAX_PREVIEW_ROWS, parsedData.totalRows)} rows):
          </Typography>

          <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.100' }}>
                    Row
                  </TableCell>
                  {parsedData.headers.map((header, idx) => (
                    <TableCell
                      key={idx}
                      sx={{
                        fontWeight: 'bold',
                        backgroundColor: 'grey.100',
                        minWidth: 120
                      }}
                    >
                      {header}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {parsedData.rows.slice(0, MAX_PREVIEW_ROWS).map((row, rowIdx) => (
                  <TableRow key={rowIdx} hover>
                    <TableCell>{rowIdx + 1}</TableCell>
                    {parsedData.headers.map((header, colIdx) => (
                      <TableCell key={colIdx}>
                        {row[header] !== null && row[header] !== undefined
                          ? String(row[header]).substring(0, 50)
                          : <Typography variant="caption" color="text.disabled">—</Typography>
                        }
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {parsedData.totalRows > MAX_PREVIEW_ROWS && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              ... and {parsedData.totalRows - MAX_PREVIEW_ROWS} more rows
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}

export default CSVUploader;